import React from 'react';
import { Trash2, Plus, Compass } from 'lucide-react';

export const DynamicDestinationsTable = ({ rows = [], onChange }) => {
  const handleAddRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `dest-${window.crypto.randomUUID()}` : `dest-${Math.random().toString(36).slice(2, 9)}`,
      destination: '',
      trips_per_month: '',
      distance_km: ''
    };
    onChange([...rows, newRow]);
  };

  const handleRemoveRow = (indexToRemove) => {
    if (rows.length <= 1) return;
    const filtered = rows.filter((_, idx) => idx !== indexToRemove);
    onChange(filtered);
  };

  const handleFieldChange = (index, field, value) => {
    const updated = rows.map((row, idx) => {
      if (idx === index) {
        return { ...row, [field]: value };
      }
      return row;
    });
    onChange(updated);
  };

  return (
    <div className="w-full">
      {/* Title & Add Row */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
            <Compass className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800 leading-tight">
              Destination / inter-facility corridors
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Frequent inter-hospital transfer destinations and long-distance patient routes.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddRow}
          aria-label="Add new destination route"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-xs font-medium cursor-pointer transition-colors shadow-2xs"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Add route</span>
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50/80 text-slate-600 font-semibold border-b border-slate-200">
              <th className="py-3 px-3 w-10 text-center">#</th>
              <th className="py-3 px-3 min-w-[220px]">Destination / Tertiary Facility / City</th>
              <th className="py-3 px-3 min-w-[130px]">Trips / Month</th>
              <th className="py-3 px-3 min-w-[150px]">Approx. Distance (km)</th>
              <th className="py-3 px-2 w-10 text-center"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((row, idx) => (
              <tr key={row.id || idx} className="hover:bg-slate-50/60 transition-colors">
                <td className="py-2.5 px-3 text-center text-slate-400 font-medium">
                  {idx + 1}
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="text"
                    placeholder="Destination facility / city"
                    aria-label={`Destination facility or city for route ${idx + 1}`}
                    value={row.destination || ''}
                    onChange={(e) => handleFieldChange(idx, 'destination', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="number"
                    min="0"
                    placeholder="Trips"
                    aria-label={`Trips per month for route ${idx + 1}`}
                    value={row.trips_per_month || ''}
                    onChange={(e) => handleFieldChange(idx, 'trips_per_month', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-3">
                  <input
                    type="number"
                    min="0"
                    placeholder="km"
                    aria-label={`Approx distance in km for route ${idx + 1}`}
                    value={row.distance_km || ''}
                    onChange={(e) => handleFieldChange(idx, 'distance_km', e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                  />
                </td>
                <td className="py-2.5 px-2 text-center">
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(idx)}
                      aria-label={`Remove destination route ${idx + 1}`}
                      className="text-slate-400 hover:text-red-600 transition-colors p-1 rounded-md cursor-pointer"
                      title="Remove row"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicDestinationsTable;
